import Point from 'esri/geometry/Point';
import SpatialReference from 'esri/SpatialReference';
import { esriQuery, esriIdentifyImageService } from 'js/utils/esriQueries';
import standardsConfig from 'js/standards.config';

import { Promise as bbPromise } from 'promise';

import { criteriaStore } from 'stores/CriteriaStore';
import { reportStore } from 'stores/ReportStore';

const generateCellValue = (value, measurement) => {
  const mps = 0.44704;
  if (value === 'NoData' || !value) {
    return 'No Data';
  } else {
    return `Vt = ${Math.round(Number(measurement === 'si' ? value * mps : value))}`;
  }
};

const queryTornado = (location, load) => {
  const { measurement, riskLevel } = criteriaStore.getState();

  const geometry = new Point(location.long, location.lat, new SpatialReference({ wkid: 4326 }));

  return new bbPromise((resolve, reject) => {
    const dataSource = {
      text: 'ASCE/SEI Standard 7-22, Figs. 32.5-1, 32.5-2, and G.2-1 through -4',
    };

    if (riskLevel < 3) {
      load.sampleData.value = [`Not Applicable to Risk Category ${riskLevel}`];
      load.details = [
        {
          type: 'singleLine',
          label: '',
          value: `Not Applicable to Risk Category ${riskLevel}`,
        },
        {
          type: 'dataSource',
          dataSource,
        },
      ];
      load.showInReport = true;
      reportStore.emitChange();
      resolve({ load: load.label });
    } else {
      try {
        const { aeValues, mriValues, tornadoProneAreas } = standardsConfig['7-22'].tornadoServices;
        esriQuery(tornadoProneAreas, { outFields: ['*'], geometry, returnGeometry: false }).then((results) => {
          if (results.features.length > 0) {
            const isTornadoProne = results.features[0].attributes.Name === 'Tornado-prone region';
            const promises = [];
            aeValues.forEach((ae) => {
              mriValues.forEach((mri) => {
                let aeVal = `${ae}SF`,
                  mriVal = `RP${mri}`;
                if (ae === 'PT') {
                  aeVal = ae;
                }
                const url = `https://gis.asce.org/arcgis/rest/services/ASCE722/t2022_${aeVal}_${mriVal}/ImageServer`;
                promises.push(
                  esriIdentifyImageService(url, { geometry })
                    .then(({ value }) => {
                      return { ae, mri, value };
                    })
                    .catch(() => {
                      return { ae, mri, value: undefined };
                    })
                );
              });
            });

            Promise.all(promises)
              .then((resultsArray) => {
                let sampleValue;
                if (riskLevel === 3) {
                  if (isTornadoProne) {
                    sampleValue = resultsArray.find((res) => res.ae === '100K' && res.mri === '1700');
                  }
                } else if (riskLevel === 4) {
                  if (isTornadoProne) {
                    sampleValue = resultsArray.find((res) => res.ae === '100K' && res.mri === '3K');
                  }
                }
                if (sampleValue && sampleValue.value) {
                  if (sampleValue.value === 'NoData') {
                    load.sampleData.value = [`No Data for Ae = ${measurement === 'si' ? '929 m^2' : '100,000 ft^2'}`];
                  } else {
                    load.sampleData.value = ['See details for V^T'];
                  }
                } else {
                  if (isTornadoProne) {
                    load.sampleData.value = [`No Data for Ae = ${measurement === 'si' ? '929 m^2' : '100,000 ft^2'}`];
                  } else {
                    load.sampleData.value = ['Not in Tornado-Prone Region'];
                    load.sampleData.value.push('See Details for optional long return period V^T');
                  }
                }

                const headerRow = [];
                let riskLevelValue;

                switch (riskLevel) {
                  case 1:
                    riskLevelValue = 'RC = I';
                    break;
                  case 2:
                    riskLevelValue = 'RC = II';
                    break;
                  case 3:
                    riskLevelValue = 'RC = III';
                    break;
                  case 4:
                    riskLevelValue = 'RC = IV';
                    break;
                }

                headerRow.push(''); // empty cell

                mriValues.forEach((mri) => {
                  if (riskLevel === 3 && mri === '1700') {
                    headerRow.push(`${riskLevelValue} (MRI = 1,700 years)`);
                  } else if (riskLevel === 4 && mri === '3K') {
                    headerRow.push(`${riskLevelValue} (MRI = 3,000 years)`);
                  } else if (mri.includes('K') && !mri.includes('3K')) {
                    headerRow.push(`MRI = ${mri.replace('K', ',000')} years`);
                  } else if (mri.includes('M')) {
                    headerRow.push(`MRI = ${mri.replace('M', ',000,000')} years`);
                  }
                });

                const rows = {
                  PT: [`Ae = ${measurement === 'si' ? '0.1' : '1'}`],
                  '2K': [`Ae = ${measurement === 'si' ? '186' : '2,000'}`],
                  '10K': [`Ae = ${measurement === 'si' ? '929' : '10,000'}`],
                  '40K': [`Ae = ${measurement === 'si' ? '3,716' : '40,000'}`],
                  '100K': [`Ae = ${measurement === 'si' ? '9,290' : '100,000'}`],
                  '250K': [`Ae = ${measurement === 'si' ? '23,226' : '250,000'}`],
                  '1M': [`Ae = ${measurement === 'si' ? '92,903' : '1,000,000'}`],
                  '4M': [`Ae = ${measurement === 'si' ? '371,612' : '4,000,000'}`],
                };

                resultsArray.forEach((result) => {
                  let value = result.value;
                  if (riskLevel === 3 && result.mri !== '3K') {
                    rows[result.ae].push(
                      result.mri === '1700' && !isTornadoProne ? 'N/A' : generateCellValue(value, measurement)
                    );
                  } else if (riskLevel === 4 && result.mri !== '1700') {
                    rows[result.ae].push(
                      result.mri === '3K' && !isTornadoProne ? 'N/A' : generateCellValue(value, measurement)
                    );
                  }
                });
                if (!isTornadoProne) {
                  load.details = [
                    {
                      type: 'singleLine',
                      label: '',
                      value: 'Not in Tornado-Prone Region',
                    },
                  ];
                }

                load.details = [
                  {
                    type: 'table',
                    label: '',
                    headerRow,
                    rows,
                  },
                  {
                    type: 'dataSource',
                    dataSource,
                  },
                ];
                load.showInReport = true;
                reportStore.emitChange();
                resolve({ load: load.label });
              })
              .catch((err) => {
                console.log('Error within handleTornado', err);
                reject(err);
              });
          } else {
            load.sampleData.value = ['Not in Tornado-Prone Region'];
            load.details = [
              {
                type: 'singleLine',
                label: '',
                value: 'Not in Tornado-Prone Region',
              },
              {
                type: 'dataSource',
                dataSource,
              },
            ];
            load.showInReport = true;
            reportStore.emitChange();
            resolve({ load: load.label });
          }
        });
      } catch (error) {
        reject({ load: load.label, error });
      }
    }
  });
};

export default queryTornado;
